import React, {useState, useEffect, useMemo} from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import "../../assets/stylesheets/FilterPane.css";

// New component to handle each nested area
function NestedArea({ node, level, parentPath, selectedFilters, handleCheckboxChange }) {
    const { t } = useTranslation(["estate"]);
    const currentPath = parentPath ? `${parentPath} > ${node.label}` : node.label;
    const hasChildren = node.children && node.children.length > 0;
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div key={currentPath} style={{ marginLeft: `${level * 20}px` }}>
            <label className="filter__option">
                <input
                    type="checkbox"
                    checked={(selectedFilters["REGION_AREA"] || []).includes(currentPath)}
                    onChange={() => handleCheckboxChange("REGION_AREA", currentPath)}
                />
                <span className="filter__option-text">{node.label}</span>
                {/*{hasChildren && (
                    <span
                        className="filter__toggle-icon"
                        onClick={() => setIsOpen(!isOpen)}
                        style={{ marginLeft: "10px", cursor: "pointer" }}
                    >
                        {isOpen ? "−" : "+"}
                    </span>
                )}*/}
            </label>
            {isOpen && hasChildren && (
                <div className="filter__nested-group">
                    {node.children
                        .filter((child) => !child.street)
                        .map((child) => (
                            <NestedArea
                                key={child.label}
                                node={child}
                                level={level + 1}
                                parentPath={currentPath}
                                selectedFilters={selectedFilters}
                                handleCheckboxChange={handleCheckboxChange}
                            />
                        ))}
                </div>
            )}
        </div>
    );
}

function FilterPane({
                        type,
                        language,
                        onFilterChange = () => {},
                        initialFilter = new Map(),
                    }) {
    const { t } = useTranslation(["estate"]);
    const [attributes, setAttributes] = useState([]);
    const [selectedFilters, setSelectedFilters] = useState({});
    const [isExpanded, setIsExpanded] = useState(true);
    const [addressSearch, setAddressSearch] = useState("");
    const navigate = useNavigate();
    const [reloadAttributes, setReloadAttributes] = useState(0);


    // Memoize initialFilters to prevent unnecessary updates
    const initialFilters = useMemo(() => {
        const filters = {};
        attributes.forEach((attr) => {
            const { attributeType } = attr;
            if (attributeType === "PRICE" || attributeType === "AREA_SIZE") {
                filters[attributeType] = {
                    min: initialFilter.get(`${attributeType}_MIN`)?.[0] || "",
                    max: initialFilter.get(`${attributeType}_MAX`)?.[0] || "",
                };
            } else if (attributeType === "FLOOR" || attributeType === "FLOORS") {
                filters[attributeType] = initialFilter.get(attributeType)?.[0] || "";
            } else if (attributeType === "REGION") {
                filters[attributeType] = initialFilter.get(attributeType)?.[0] || "";
                filters["REGION_AREA"] = initialFilter.get("REGION_AREA") || [];
                filters["ADDRESS"] = initialFilter.get("ADDRESS") || [];
            } else {
                filters[attributeType] = initialFilter.get(attributeType) || [];
            }
        });
        return filters;
    }, [attributes, initialFilter]);


    useEffect(() => {
        const timeout = setTimeout(() => {
            handleFilterSubmit();
        }, 300); // debounce delay in ms

        return () => clearTimeout(timeout); // cleanup on new change
    }, [selectedFilters]);

    useEffect(() => {
        const fetchAttributes = async () => {
            try {
                const finalType = type === "search" ? "all" : `${type}`;
                const listingType = initialFilter.get("LISTING_TYPE")?.[0];
                let url = `${process.env.REACT_APP_MY_API_KAY}/attributes?language=${language}&type=${finalType}`;
                if (listingType) {
                    url += `&listingType=${listingType}`;
                }
                const res = await axios.get(url);
                setAttributes(res.data);
            } catch (error) {
                console.error("Error fetching attributes:", error);
            }
        };
        fetchAttributes();
    }, [type, language, reloadAttributes]);

    useEffect(() => {
        if (window.innerWidth <= 768) {
            // Typical breakpoint for mobile
            setIsExpanded(false);
        }
    }, []);

    useEffect(() => {
        // Only set selectedFilters if not already initialized
        if (Object.keys(selectedFilters).length === 0) {
            console.log("FilterPane: Initial selectedFilters:", initialFilters);
            setSelectedFilters(initialFilters);
        }
    }, [initialFilters]);

    const togglePane = () => {
        setIsExpanded((prev) => !prev);
    };

    const handlePriceRangeChange = (e, rangeKey) => {
        const value = e.target.value;
        setSelectedFilters((prev) => ({
            ...prev,
            PRICE: { ...prev.PRICE, [rangeKey]: value },
        }));
    };

    const handleAreaSizeRangeChange = (e, rangeKey) => {
        const value = e.target.value;
        setSelectedFilters((prev) => ({
            ...prev,
            AREA_SIZE: { ...prev.AREA_SIZE, [rangeKey]: value },
        }));
    };

    const handleInputChange = (attrKey, e) => {
        setSelectedFilters((prev) => ({
            ...prev,
            [attrKey]: e.target.value,
        }));
    };

    const handleCheckboxChange = (attrKey, value) => {
        setSelectedFilters((prev) => {
            const current = prev[attrKey] || [];
            if (current.includes(value)) {
                return { ...prev, [attrKey]: current.filter((v) => v !== value) };
            } else {
                return { ...prev, [attrKey]: [...current, value] };
            }
        });
    };

    const handleSelectChange = (attrKey, e) => {
        const value = e.target.value;
        setSelectedFilters((prev) => {
            const newFilters = { ...prev, [attrKey]: value ? [value] : [] };
            if (attrKey === "REGION") {
                newFilters["REGION_AREA"] = [];
                newFilters["ADDRESS"] = [];
            }
            return newFilters;
        });
    };

    const handleFilterSubmit = () => {
        const filterMap = new Map();
        const queryMap = {};
        Object.entries(selectedFilters).forEach(([key, value]) => {
            if (key === "PRICE" && (value.min || value.max)) {
                if (value.min) {
                    filterMap.set("PRICE_MIN", [value.min]);
                    queryMap["PRICE_MIN"] = value.min;
                }
                if (value.max) {
                    filterMap.set("PRICE_MAX", [value.max]);
                    queryMap["PRICE_MAX"] = value.max;
                }
            } else if (key === "AREA_SIZE" && (value.min || value.max)) {
                if (value.min) {
                    filterMap.set("AREA_SIZE_MIN", [value.min]);
                    queryMap["AREA_SIZE_MIN"] = value.min;
                }
                if (value.max) {
                    filterMap.set("AREA_SIZE_MAX", [value.max]);
                    queryMap["AREA_SIZE_MAX"] = value.max;
                }
            } else if ((key === "FLOOR" || key === "FLOORS") && value !== "") {
                filterMap.set(key, [value]);
                queryMap[key] = value;
            } else if (
                (key === "REGION" || key === "REGION_AREA" || key === "ADDRESS") &&
                Array.isArray(value) &&
                value.length > 0
            ) {
                filterMap.set(key, value);
                queryMap[key] = value.join(",");
            } else if (Array.isArray(value) && value.length > 0) {
                filterMap.set(key, value);
                queryMap[key] = value.join(",");
            }
        });
        console.log("FilterPane: Applying filterMap:", [...filterMap.entries()]);
        onFilterChange(filterMap);
        const queryString = new URLSearchParams(queryMap).toString();
        navigate(`/${language}/${type}?${queryString}`, { replace: true });


        // trigger reload
        setReloadAttributes(prev => prev + 1);
    };

    const handleResetFilters = () => {
        const resetFilters = {};
        attributes.forEach((attr) => {
            const { attributeType } = attr;
            if (attributeType === "PRICE" || attributeType === "AREA_SIZE") {
                resetFilters[attributeType] = { min: "", max: "" };
            } else if (attributeType === "FLOOR" || attributeType === "FLOORS") {
                resetFilters[attributeType] = "";
            } else if (attributeType === "REGION") {
                resetFilters[attributeType] = "";
                resetFilters["REGION_AREA"] = [];
                resetFilters["ADDRESS"] = [];
            } else {
                resetFilters[attributeType] = [];
            }
        });
        setSelectedFilters(resetFilters);
        setAddressSearch("");
        onFilterChange(new Map());
        navigate(`/${language}/${type}`, { replace: true });
    };

    const renderRoomsOptions = () => {
        const rooms = [1, 2, 3, 4, "4+"];
        return (
            <select
                className="filter__select"
                value={selectedFilters.ROOMS?.[0] || ""}
                onChange={(e) => handleSelectChange("ROOMS", e)}
            >
                <option value="">{t("selectOption")}</option>
                {rooms.map((room) => (
                    <option key={room} value={room}>
                        {room}
                    </option>
                ))}
            </select>
        );
    };

    const getAllStreets = (values, regionFilter) => {
        let streets = [];
        values.forEach((region) => {
            if (!regionFilter || region.label === regionFilter) {
                const collectStreets = (node) => {
                    if (node.street) {
                        streets.push(node.label);
                    }
                    if (node.children) {
                        node.children.forEach(collectStreets);
                    }
                };
                collectStreets(region);
            }
        });
        return streets.filter((street) =>
            street.toLowerCase().includes(addressSearch.toLowerCase())
        );
    };

    const renderAttribute = (attribute) => {
        const { attributeType, attributes: { translation, values } } = attribute;
        const key = attributeType;

        if (key === "REGION") {
            const selectedRegion = selectedFilters[key]?.[0] || "";
            const regionWithChildren = values.find(
                (region) => region.label === selectedRegion && region.children.length > 0
            );
            const areas = regionWithChildren ? regionWithChildren.children : [];
            const streets = getAllStreets(values, selectedRegion);

            return (
                <div key={key} className="filter__group">
                    <h4 className="filter__group-title">{translation}</h4>
                    <select
                        className="filter__select"
                        value={selectedRegion}
                        onChange={(e) => handleSelectChange(key, e)}
                    >
                        <option value="">{t("selectOption")}</option>
                        {values.map((region) => (
                            <option key={region.label} value={region.label}>
                                {region.label}
                            </option>
                        ))}
                    </select>
                    {areas.length > 0 && (
                        <div className="filter__group">
                            <h4 className="filter__group-title">{t("regionArea")}</h4>
                            <div
                                className="filter__checkbox-group"
                                style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                    border: "1px solid #ccc",
                                    padding: "10px",
                                    borderRadius: "4px"
                                }}
                            >
                                {areas
                                    .filter((node) => !node.street)
                                    .map((node) => (
                                        <NestedArea
                                            key={node.label}
                                            node={node}
                                            level={0}
                                            parentPath=""
                                            selectedFilters={selectedFilters}
                                            handleCheckboxChange={handleCheckboxChange}
                                        />
                                    ))}
                            </div>
                        </div>
                    )}
                    <div className="filter__group">
                        <h4 className="filter__group-title">{t("address")}</h4>
                        <input
                            type="text"
                            className="filter__input"
                            placeholder={t("searchAddress")}
                            value={addressSearch}
                            onChange={(e) => setAddressSearch(e.target.value)}
                        />
                        {addressSearch && streets.length > 0 && (
                            <div
                                className="filter__checkbox-group"
                                style={{
                                    maxHeight: "200px",
                                    overflowY: "auto",
                                    border: "1px solid #ccc",
                                    padding: "10px",
                                    borderRadius: "4px"
                                }}
                            >
                                {(() => {
                                    const uniqueStreets = [...new Set(streets)]; // Remove duplicates
                                    const filteredStreets = uniqueStreets.filter((street) =>
                                        street.toLowerCase().includes(addressSearch.toLowerCase())
                                    );
                                    return filteredStreets.map((street) => (
                                        <label key={street} className="filter__option">
                                            <input
                                                type="checkbox"
                                                checked={(selectedFilters["ADDRESS"] || []).includes(street)}
                                                onChange={() => handleCheckboxChange("ADDRESS", street)}
                                            />
                                            <span className="filter__option-text">{street}</span>
                                        </label>
                                    ));
                                })()}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        if (key === "CURRENCY") {
            return (
                <div key={key} className="filter__group-grid">
                    <h4 className="filter__group-title">{translation}</h4>
                    <h4 className="filter__group-title"></h4>
                    {values.length <= 4 ? (
                        values.map((value) => (
                            <label key={value} className="filter__option">
                                <input
                                    type="checkbox"
                                    checked={(selectedFilters[key] || []).includes(value)}
                                    onChange={() => handleCheckboxChange(key, value)}
                                />
                                <span className="filter__option-text">{value}</span>
                            </label>
                        ))
                    ) : (
                        <select
                            className="filter__select"
                            value={selectedFilters[key]?.[0] || ""}
                            onChange={(e) => handleSelectChange(key, e)}
                        >
                            <option value="">{t("selectOption")}</option>
                            {values.map((value) => (
                                <option key={value} value={value}>
                                    {value}
                                </option>
                            ))}
                        </select>
                    )}
                </div>
            );
        }

        return (
            <div key={key} className="filter__group">
                <h4 className="filter__group-title">{translation}</h4>
                {key === "PRICE" ? (
                    <div className="filter__range-group">
                        <input
                            type="number"
                            className="filter__input"
                            placeholder={t("minPrice")}
                            value={selectedFilters[key]?.min || ""}
                            onChange={(e) => handlePriceRangeChange(e, "min")}
                            min="0"
                        />
                        <input
                            type="number"
                            className="filter__input"
                            placeholder={t("maxPrice")}
                            value={selectedFilters[key]?.max || ""}
                            onChange={(e) => handlePriceRangeChange(e, "max")}
                            min="0"
                        />
                    </div>
                ) : key === "AREA_SIZE" ? (
                    <div className="filter__range-group">
                        <input
                            type="number"
                            className="filter__input"
                            placeholder={t("minAreaSize")}
                            value={selectedFilters[key]?.min || ""}
                            onChange={(e) => handleAreaSizeRangeChange(e, "min")}
                            min="0"
                        />
                        <input
                            type="number"
                            className="filter__input"
                            placeholder={t("maxAreaSize")}
                            value={selectedFilters[key]?.max || ""}
                            onChange={(e) => handleAreaSizeRangeChange(e, "max")}
                            min="0"
                        />
                    </div>
                ) : key === "FLOOR" ? (
                    <input
                        type="number"
                        className="filter__input"
                        placeholder={t("floor")}
                        value={selectedFilters[key] || ""}
                        onChange={(e) => handleInputChange(key, e)}
                    />
                ) : key === "FLOORS" ? (
                    <input
                        type="number"
                        className="filter__input"
                        placeholder={t("floors")}
                        value={selectedFilters[key] || ""}
                        onChange={(e) => handleInputChange(key, e)}
                        min="1"
                    />
                ) : key === "ROOMS" ? (
                    renderRoomsOptions()
                ) : values.length <= 4 ? (
                    values.map((value) => (
                        <label key={value} className="filter__option">
                            <input
                                type="checkbox"
                                checked={(selectedFilters[key] || []).includes(value)}
                                onChange={() => handleCheckboxChange(key, value)}
                            />
                            <span className="filter__option-text">{value}</span>
                        </label>
                    ))
                ) : (
                    <select
                        className="filter__select"
                        value={selectedFilters[key]?.[0] || ""}
                        onChange={(e) => handleSelectChange(key, e)}
                    >
                        <option value="">{t("selectOption")}</option>
                        {values.map((value) => (
                            <option key={value} value={value}>
                                {value}
                            </option>
                        ))}
                    </select>
                )}
            </div>
        );
    };

    return (
        <div>
            <h3
                className="filter__pane-title filter__pane-title--toggle"
                onClick={togglePane}
            >
                {t("filters")}
                <span className="filter__toggle-icon">{isExpanded ? "−" : "+"}</span>
            </h3>
            {isExpanded && (
                <div className="filter__pane-content">
                    {attributes.map((attribute) => renderAttribute(attribute))}
                    <div className="filter__buttons">
                        <button className="filter__apply-button" onClick={handleFilterSubmit}>
                            {t("applyFilters")}
                        </button>
                        <button className="filter__reset-button" onClick={handleResetFilters}>
                            {t("resetFilters")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FilterPane;